import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import useUser from "../hooks/UseUser";

// Landing page hit when an iPhone Camera scan opens
//   https://dogcontroller.com/join?code=<familyCode>&role=member|editor
// in Safari. We can't auto-join here for unauthenticated visitors —
// joinFamilyWithCode runs as the signed-in user, so we first gate on
// Cognito auth, then either deep-link into the installed app or guide
// the user to install it.
export const JOIN_RETURN_KEY = "dogcontroller.joinReturnUrl";

export function JoinPage() {
  const navigate = useNavigate();
  const { user } = useUser();
  const [code, setCode] = useState<string | undefined>(undefined);
  const [role, setRole] = useState<string | undefined>(undefined);
  const [triedDeepLink, setTriedDeepLink] = useState(false);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    setCode(params.get("code") ?? undefined);
    setRole(params.get("role") ?? undefined);
  }, []);

  // Only fire the app-deep-link when the user is authenticated. For
  // signed-out visitors we want them on the sign-in flow first.
  useEffect(() => {
    if (!code || triedDeepLink || !user) {
      return;
    }
    setTriedDeepLink(true);
    window.location.href = `myapp://join?code=${encodeURIComponent(
      code
    )}&role=${encodeURIComponent(role ?? "member")}`;
  }, [code, role, triedDeepLink, user]);

  const openInApp = () => {
    if (!code) return;
    window.location.href = `myapp://join?code=${encodeURIComponent(
      code
    )}&role=${encodeURIComponent(role ?? "member")}`;
  };

  const goSignIn = () => {
    // Stash where to send the user back after sign-in completes. The
    // sign-in handler in App.tsx watches sessionStorage for this key.
    window.sessionStorage.setItem(
      JOIN_RETURN_KEY,
      window.location.pathname + window.location.search
    );
    navigate("/account");
  };

  if (!code) {
    return (
      <div style={{ padding: 24, fontFamily: "sans-serif" }}>
        <h2>Join Dog Controller</h2>
        <p>
          This link is missing a family code. Please scan the QR code shown in
          your family admin's Family screen again.
        </p>
      </div>
    );
  }

  // user === undefined → still loading. Render nothing to avoid flashing
  // the wrong UI before we know.
  if (user === undefined) {
    return null;
  }

  if (user === null) {
    return (
      <div
        style={{
          padding: 24,
          maxWidth: 480,
          margin: "0 auto",
          fontFamily: "sans-serif",
          textAlign: "center",
        }}
      >
        <h2>Join Dog Controller</h2>
        <p>
          You've been invited to join a family as <strong>{role ?? "member"}</strong>.
        </p>
        <p style={{ color: "#666" }}>
          Sign in or create an account to accept this invitation.
        </p>
        <button
          onClick={goSignIn}
          style={{
            marginTop: 16,
            padding: "12px 24px",
            fontSize: 16,
            backgroundColor: "#ff8c00",
            color: "white",
            border: "none",
            borderRadius: 8,
            cursor: "pointer",
          }}
        >
          Sign in / Create account
        </button>
        <p style={{ marginTop: 32, fontSize: 12, color: "#999" }}>
          Already have the app installed? You can also open it directly:
        </p>
        <button
          onClick={openInApp}
          style={{
            marginTop: 8,
            padding: "8px 16px",
            fontSize: 14,
            backgroundColor: "transparent",
            color: "#ff8c00",
            border: "1px solid #ff8c00",
            borderRadius: 8,
            cursor: "pointer",
          }}
        >
          Open in Dog Controller app
        </button>
      </div>
    );
  }

  return (
    <div
      style={{
        padding: 24,
        maxWidth: 480,
        margin: "0 auto",
        fontFamily: "sans-serif",
        textAlign: "center",
      }}
    >
      <h2>Join Dog Controller</h2>
      <p>
        You've been invited to join a family as <strong>{role ?? "member"}</strong>.
      </p>

      <button
        onClick={openInApp}
        style={{
          marginTop: 16,
          padding: "12px 24px",
          fontSize: 16,
          backgroundColor: "#ff8c00",
          color: "white",
          border: "none",
          borderRadius: 8,
          cursor: "pointer",
        }}
      >
        Open in Dog Controller app
      </button>

      <p style={{ marginTop: 24, color: "#666" }}>Don't have the app yet?</p>
      <div style={{ display: "flex", justifyContent: "center", gap: 12 }}>
        <a
          href="https://apps.apple.com/app/dog-controller"
          target="_blank"
          rel="noopener noreferrer"
        >
          App Store
        </a>
        <a
          href="https://play.google.com/store/apps/details?id=com.dogcontroller"
          target="_blank"
          rel="noopener noreferrer"
        >
          Google Play
        </a>
      </div>

      <p style={{ marginTop: 32, fontSize: 12, color: "#999" }}>
        After installing, scan the QR code again or paste this code manually in
        Profile → Join family:
      </p>
      <code
        style={{
          display: "inline-block",
          marginTop: 8,
          padding: "6px 12px",
          backgroundColor: "#f0f0f0",
          borderRadius: 6,
          fontSize: 14,
        }}
      >
        {code}
      </code>
    </div>
  );
}
